import React, { useMemo } from 'react';

import { Stack } from '@/components/layout';
import { Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import type { PlayerProfileData, PlayerProgressItem } from '@/features/player/components/types';
import { useScaling } from '@/hooks';
import { colors } from '@/theme/colors';

import { CompletedLevelCard } from './completed-level-card';
import { CurrentLevelBadgeRow } from './current-level-badge-row';
import { formatDisplayDate, parseLevelNumber } from './progress-tracker.utils';
import { useStyles } from './styles';

export function PlayerProfileProgressTrackerContent({ profile }: { profile: PlayerProfileData }) {
  const styles = useStyles();
  const { isTablet, scale } = useScaling();
  const copy = STRINGS.player.profile.progressTrackerContent;

  const progress: PlayerProgressItem[] = useMemo(() => profile.progress ?? [], [profile.progress]);
  const currentLevelNumber = useMemo(() => {
    const n = Number(profile.level ?? 1);
    return Number.isFinite(n) && n >= 1 ? n : 1;
  }, [profile.level]);

  // iOS behavior: current level is driven by the player's `level` field (not inferred from progress).
  const currentLevelItem = useMemo(() => {
    return progress.find(p => parseLevelNumber(p.name) === currentLevelNumber) ?? null;
  }, [progress, currentLevelNumber]);

  // iOS behavior: show "completed levels" only for levels below the current level.
  const completedLevels = useMemo(() => {
    return progress
      .filter(p => {
        const n = parseLevelNumber(p.name);
        if (!n) return false;
        // iOS `pastLevelView`: renders every level in 1..<(player.level), regardless of `progress[i].completed`.
        return n < currentLevelNumber;
      })
      .slice()
      .sort((a, b) => (parseLevelNumber(a.name) ?? 0) - (parseLevelNumber(b.name) ?? 0));
  }, [progress, currentLevelNumber]);
  const currentLevelColor = useMemo(() => {
    return colors[`Level ${currentLevelNumber}` as keyof typeof colors] ?? colors['Level 9'];
  }, [currentLevelNumber]);

  const lastAttendedLabel = useMemo(() => {
    const display = formatDisplayDate(profile.lastAttended);
    return display ?? '-';
  }, [profile.lastAttended]);

  const showEmpty = progress.length === 0;

  return (
    <Stack
      flexGrow={1}
      backgroundColor={isTablet ? colors['1'] : colors['2']}
      paddingX={scale({ phone: 18, tablet: 24 })}
      paddingTop={scale({ phone: 20, tablet: 16 })}
      paddingBottom={scale(24)}
      gap={scale({ phone: 12, tablet: 8 })}>
      <Text
        variant={'Font Montserrat/Subtitle/Minimize 1 Bold 11px'}
        fontWeight={isTablet ? undefined : '800'}
        style={styles.sectionTitle}>
        {copy.currentLevelTitle}
      </Text>

      <Stack style={styles.card}>
        <Stack direction="row" style={styles.cardBodyRow}>
          <Stack
            style={[
              styles.cardAccent,
              {
                backgroundColor: currentLevelColor,
                borderTopLeftRadius: scale(2),
                borderBottomLeftRadius: scale(2),
              },
            ]}
          />
          <Stack flex={1}>
            <Stack
              style={styles.cardHeaderRow}
              paddingX={scale(9)}
              paddingY={scale(12)}
              gap={scale(4)}>
              <Text
                fontWeight={'800'}
                fontSize={scale(10)}
                fontFamily="Montserrat"
                style={[styles.levelTitle, { color: currentLevelColor }]}>
                {`LEVEL ${currentLevelNumber}`}
              </Text>
              <Text
                fontWeight={'400'}
                fontSize={scale(8)}
                fontFamily="Inter"
                style={styles.subText}>
                {`${copy.lastClassAttendedLabel}${
                  lastAttendedLabel === '-' ? ' -' : ` ${lastAttendedLabel}`
                }`}
              </Text>
            </Stack>

            {showEmpty ? null : (
              <>
                <Stack style={styles.divider} />
                <Stack>
                  {(currentLevelItem?.badges ?? []).map((badge, idx) => (
                    <CurrentLevelBadgeRow
                      key={`${badge.name}-${idx}`}
                      badge={badge}
                      levelColor={currentLevelColor}
                      isLast={idx === (currentLevelItem?.badges?.length ?? 0) - 1}
                    />
                  ))}
                </Stack>
              </>
            )}
          </Stack>
        </Stack>
      </Stack>
      <Stack style={styles.sectionDivider} />

      <Text
        variant={'Font Montserrat/Subtitle/Minimize 1 Bold 11px'}
        fontWeight={isTablet ? undefined : '800'}
        style={styles.sectionTitle}>
        {copy.completedLevelsTitle}
      </Text>
      {completedLevels.length === 0 ? null : (
        <>
          <Stack gap={scale(12)}>
            {completedLevels.map((lvl, idx) => (
              <CompletedLevelCard key={`${lvl.name}-${idx}`} item={lvl} />
            ))}
          </Stack>
        </>
      )}
    </Stack>
  );
}
